import { useState } from "react";
import { Navigation } from "@/components/layout/Navigation";
import { WelcomeHeader } from "@/components/dashboard/WelcomeHeader";
import { NewsFeed } from "@/components/dashboard/NewsFeed";
import { WelcomeHeaderLoading, NewsFeedLoading } from "@/components/dashboard/LoadingStates";
import { IntroTour } from "@/components/intro/IntroTour";
import { TutorialButton } from "@/components/intro/TutorialButton";
import { WeeklyStatsSection } from "@/components/dashboard/sections/WeeklyStatsSection";
import { TodayRoutinesSection } from "@/components/dashboard/sections/TodayRoutinesSection";
import { RewardsSection } from "@/components/dashboard/sections/RewardsSection";
import { useBadgeAward } from "@/hooks/useBadgeAward";
import { supabase } from "@/integrations/supabase/client";
import { useDashboardData } from "@/hooks/dashboard/useDashboardData";
import { useCacheInvalidation } from "@/hooks/useCacheInvalidation";
import { useRecepcionistaRedirect } from "@/hooks/useRecepcionistaRedirect";

export const DashboardLayout = () => {
  const [runTutorial, setRunTutorial] = useState(false);
  
  // Initialize cache invalidation to handle user switches
  useCacheInvalidation();
  const {
    profile,
    profileLoading,
    posts,
    postsLoading,
    routines,
    allUserRoutines,
    todayRoutines,
    hasAssignedRoutines,
    completedRoutines,
    isLoadingRoutines,
    currentGym
  } = useDashboardData();

  const { isRecepcionista } = useRecepcionistaRedirect();
  const canActAsRecepcionista =
    profile?.role === "profesor" &&
    !!currentGym?.name?.toUpperCase().includes("MASTERGYM") &&
    isRecepcionista === true;

  const handleTutorialClick = async () => {
    if (profile?.id) {
      await supabase
        .from('user_settings')
        .update({ has_seen_tutorial: false })
        .eq('user_id', profile.id);
      
      setRunTutorial(true);
    }
  };

  // Add the badge award hook
  useBadgeAward();

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      
      <main className="w-full pb-24 relative z-10 px-3 sm:px-4 lg:px-6">
        {/* Welcome Header Section */}
        {profileLoading ? (
          <WelcomeHeaderLoading />
        ) : (
          <div className="welcome-header pb-2">
            <div className="mx-auto max-w-4xl">
              <WelcomeHeader
                profile={profile}
                gymLogo={currentGym?.logo_url}
                gymName={currentGym?.name}
                canActAsRecepcionista={canActAsRecepcionista}
              />
            </div>
          </div>
        )}

        {/* News Feed Section */}
        {postsLoading ? (
          <NewsFeedLoading />
        ) : (
          <section className="news-feed py-4">
            <div className="mx-auto max-w-4xl">
              <NewsFeed posts={posts} />
            </div>
          </section>
        )}

        {/* Today's Routines Section */}
        <section className="routines-section py-6 animate-fade-in stagger-3">
          <div className="mx-auto max-w-4xl">
            <TodayRoutinesSection 
              todayRoutines={todayRoutines}
              hasAssignedRoutines={hasAssignedRoutines}
              completedRoutines={completedRoutines}
              isLoadingRoutines={isLoadingRoutines}
            />
          </div>
        </section>

        {/* Section Divider */}
        <div className="mx-auto max-w-4xl">
          <div className="section-divider" />
        </div>

        {/* Weekly Stats Section */}
        <section className="stats-section animate-fade-in stagger-4">
          <WeeklyStatsSection routines={allUserRoutines || []} />
        </section>

        {/* Section Divider */}
        <div className="mx-auto max-w-4xl">
          <div className="section-divider" />
        </div>

        {/* Rewards Section */}
        <div className="animate-fade-in stagger-5">
          <RewardsSection />
        </div>

        {/* Tutorial Button */}
        <div className="pb-8">
          <div className="mx-auto max-w-4xl">
            <TutorialButton onClick={handleTutorialClick} />
          </div>
        </div>
        
        <Navigation />
      </main>
      <IntroTour run={runTutorial} setRun={setRunTutorial} />
    </div>
  );
};
